/* eslint-disable react/jsx-key */
import React, {
  createRef,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";
import { useEffectOnce } from "react-use";
import { useHistory, Link } from "react-router-dom";
import {
  useColumnOrder,
  useExpanded,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
  useGroupBy,
  useBlockLayout
} from "react-table";
import PropTypes from "prop-types";
import { TableVirtuoso } from "react-virtuoso";

import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import ShareIcon from "@mui/icons-material/Share";
import { Checkbox } from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import ProgressIndicatorOverlay from "components/ProgressIndicatorOverlay";
import { Pagination } from "components/SharedTable/Pagination";
import { TablePaginationActions } from "components/SharedTable/TablePaginationActions";
import { AddCaseButton } from "components/CaseManagement/AddCaseButton";
import { AddToProjectDropdown } from "components/CaseManagement/AddToProjectDropdown";
import { CaseTableOptions } from "components/CasesTable/CaseTableOptions";
import { CaseTableSettingsButton } from "components/CaseManagement/CaseTableSettingsButton";
import { PrintCasesDialog } from "components/CaseManagement/PrintCasesDialog";
import { CreateMultipleCasesDialog } from "components/CaseManagement/CreateMultipleCasesDialog";

import useDumbPageTitle from "hooks/useDumbPageTitle";
import { useTrackPageView } from "hooks/useTrackPageView";
import { useExportCases } from "hooks/useExport";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { CaseManagementParamsContext } from "contexts/CaseManagementParamsContext";

import {
  CONDITIONALLY_HIDDEN_COLUMNS,
  CONSTANT_HIDDEN_COLUMNS,
  DEFAULT_SORT,
  INITIAL_HIDDEN_COLUMNS
} from "components/CasesTable/CaseManagementTableColumns";

import { styled } from "@mui/material/styles";
import { CaseTableFilters } from "components/CasesTable/CaseTableFilters";
import { FeaturesContext } from "contexts/FeaturesContext";
import { useLoadCounter } from "hooks/useLoadCounter";

const PAGE_TITLE = "Case Management";

function CaseManagementPage() {
  useDumbPageTitle(PAGE_TITLE);
  useTrackPageView(PAGE_TITLE);
  const history = useHistory();
  const tableRef = createRef();
  const [exportCases, { loading: exportLoading }] = useExportCases();
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const {
    DEFAULT_ROWS_PER_PAGE,
    GROUPING_ROWS_PER_PAGE,
    casesLoading,
    columnsLoading,
    tableState,
    setTableState,
    fetchCases
  } = useContext(CaseManagementParamsContext);

  const tableLoading = casesLoading || columnsLoading;
  const tableLoadCount = useLoadCounter(tableLoading);

  const [selectedCaseIds, setSelectedCaseIds] = useState(new Set());

  // Initialize the CM table by URL-param or Context-provider
  useEffectOnce(() => {
    if (history?.location?.search) {
      // When user get this page with shared link
      const searchParams = new URLSearchParams(history.location.search);

      const groupByParams = searchParams.get("groupBy");
      const sortByParams = searchParams.get("sortBy");
      const filterParams = searchParams.get("filters");
      const columnParams = searchParams.get("columns");
      const pageParams = searchParams.get("page");
      const pageSizeParams = searchParams.get("pagesize");

      let groupBy = [];
      let sortBy = [DEFAULT_SORT];
      let filters = {};
      let hiddenColumns = [];
      let columnOrder = [];
      let pageIndex = 0;
      let rowsPerPage = DEFAULT_ROWS_PER_PAGE;

      try {
        if (groupByParams) {
          groupBy = JSON.parse(decodeURI(groupByParams));
        }

        if (sortByParams) {
          sortBy = JSON.parse(decodeURI(sortByParams));
        }

        if (filterParams) {
          filters = JSON.parse(decodeURI(filterParams));
        }

        if (columnParams) {
          const columns = JSON.parse(decodeURI(columnParams));

          hiddenColumns =
            columns?.reduce((result, column) => {
              if (column.charAt(0) === "-") result.push(column.substring(1));
              return result;
            }, []) ?? [];

          columnOrder =
            columns?.map(column =>
              column.charAt(0) === "-" ? column.substring(1) : column
            ) ?? [];
        }

        if (pageParams) {
          pageIndex = JSON.parse(decodeURI(pageParams));
        }

        if (pageSizeParams) {
          rowsPerPage = JSON.parse(decodeURI(pageSizeParams));
        }

        setTableState(state => ({
          ...state,
          initialFetch: true,
          pageIndex: pageIndex ?? state.pageIndex,
          rowsPerPage: rowsPerPage ?? state.rowsPerPage,
          groupBy: groupBy ?? state.groupBy,
          sortBy: sortBy ?? state.sortBy,
          filters: Object.keys(filters).length ? filters : state.filters,
          hiddenColumns: hiddenColumns ?? state.hiddenColumns,
          columnOrder: columnOrder ?? state.columnOrder
        }));
      } catch (e) {
        addSnackbarToQueue({
          message: "URL parameters not recognised.",
          severity: "error"
        });
        console.error(e);
      }
    } else {
      // Reset page index
      setTableState(state => ({
        ...state,
        pageIndex: 0
      }));
    }

    fetchCases(true);
  });

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    visibleColumns,
    allColumns
  } = useTable(
    {
      columns: useMemo(() => tableState.columns, [tableState.columns]),
      data: useMemo(() => tableState.cases, [tableState.cases]),
      manualSortBy: true,
      manualFilters: true,
      manualPagination: true,
      autoResetPage: false,
      autoResetSortBy: false,
      autoResetFilters: false,
      useControlledState: state =>
        useMemo(
          () => {
            let hiddenColumns =
              tableState.hiddenColumns.length > 0
                ? tableState.hiddenColumns
                : [
                    ...CONSTANT_HIDDEN_COLUMNS,
                    ...CONDITIONALLY_HIDDEN_COLUMNS,
                    ...INITIAL_HIDDEN_COLUMNS
                  ];

            return {
              ...state,
              sortBy: tableState.sortBy,
              hiddenColumns: hiddenColumns,
              columnOrder: tableState.columnOrder,
              groupBy: tableState.groupBy,
              expanded: tableState.expanded
            };
          },
          // eslint-disable-next-line react-hooks/exhaustive-deps
          [tableState]
        )
    },
    useColumnOrder,
    useGroupBy,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    useBlockLayout
  );

  // TODO: Need to handle this correctly. Only allow subrows to be selected.
  const selectedCases = useMemo(() => {
    // Map case_id to id to match Case interface
    return page
      .filter(row => selectedCaseIds.has(row.original?.case_id))
      .map(row => ({
        ...row.original,
        id: row.original?.case_id
      }));
  }, [page, selectedCaseIds]);

  const getUrlParams = useCallback(() => {
    const searchParams = new URLSearchParams();
    const columnParams = allColumns.map(({ id, isVisible }) =>
      isVisible ? id : `-${id}`
    );

    searchParams.set("groupBy", encodeURI(JSON.stringify(tableState.groupBy)));
    searchParams.set("sortBy", encodeURI(JSON.stringify(tableState.sortBy)));
    searchParams.set("filters", encodeURI(JSON.stringify(tableState.filters)));
    searchParams.set("columns", encodeURI(JSON.stringify(columnParams)));
    searchParams.set("page", encodeURI(JSON.stringify(tableState.pageIndex)));
    searchParams.set(
      "pagesize",
      encodeURI(JSON.stringify(tableState.rowsPerPage))
    );
    return searchParams;
  }, [
    allColumns,
    tableState.filters,
    tableState.sortBy,
    tableState.pageIndex,
    tableState.rowsPerPage,
    tableState.groupBy
  ]);

  const setFilters = useCallback(
    (title, values) => {
      setTableState(state => ({
        ...state,
        filters: { ...state.filters, [title]: values },
        pageIndex: 0
      }));
      fetchCases();
    },
    [setTableState, fetchCases]
  );

  const setFilterGroups = useCallback(
    filters => {
      setTableState(state => ({
        ...state,
        filters: { ...filters },
        pageIndex: 0
      }));
      fetchCases();
    },
    [setTableState, fetchCases]
  );

  const clearFilters = useCallback(() => {
    setTableState(state => ({
      ...state,
      filters: {},
      pageIndex: 0
    }));
    fetchCases();
  }, [setTableState, fetchCases]);

  const setSortBy = useCallback(
    ({ id, disableSortBy, isSortedDesc }, event) => {
      if (disableSortBy) return;

      let desc = false;
      if (isSortedDesc !== undefined) {
        desc = !isSortedDesc;
      }

      if (event.shiftKey) {
        // To enable multi column sorting, hold shift key down to add clicked column to existing sort.
        setTableState(state => {
          const existingSortBy = state.sortBy || []; // Get the current sorting state of the table.

          const sortBy = existingSortBy.filter(sort => sort.id !== id); // Filter out columns that are already sorted.
          sortBy.push({ id, desc }); // Add the new sort to the start of sortBy array.

          return {
            ...state,
            sortBy,
            pageIndex: 0
          };
        });
      } else {
        // If no modifier key held down, clear the existing sort and sort only by the clicked column.
        setTableState(state => {
          return {
            ...state,
            sortBy: [{ id, desc }],
            pageIndex: 0
          };
        });
      }

      fetchCases();
    },
    [setTableState, fetchCases]
  );

  const setGroupBy = useCallback(
    column => {
      setTableState(state => {
        const groupBy = [...state.groupBy];

        // If the column is already in groupBy, remove it
        if (groupBy.includes(column.id)) {
          groupBy.splice(groupBy.indexOf(column.id), 1);
        } else {
          // Otherwise, add it to groupBy
          groupBy.push(column.id);
        }

        let rowsPerPage =
          groupBy.length > 0 ? GROUPING_ROWS_PER_PAGE : DEFAULT_ROWS_PER_PAGE;

        return {
          ...state,
          pageIndex: 0,
          rowsPerPage,
          groupBy
        };
      });
      // False will unset the currently saved table (clears the saved table)
      fetchCases(false);
    },

    [GROUPING_ROWS_PER_PAGE, DEFAULT_ROWS_PER_PAGE, setTableState, fetchCases]
  );

  const toggleRowExpanded = useCallback(
    rowId => {
      setTableState(state => {
        const expanded = { ...state.expanded };

        // If the row is already expanded, remove it
        if (expanded[rowId]) {
          delete expanded[rowId];
        } else {
          // Otherwise, add it to expanded
          expanded[rowId] = true;
        }

        return {
          ...state,
          expanded
        };
      });
    },
    [setTableState]
  );

  const setColumns = useCallback(
    (columnOrder, hiddenColumns) => {
      setTableState(state => ({
        ...state,
        hiddenColumns: hiddenColumns,
        columnOrder: columnOrder
      }));
      fetchCases();
    },
    [setTableState, fetchCases]
  );

  const handleChangePage = useCallback(
    (_event, pageIndex) => {
      setTableState(state => ({
        ...state,
        pageIndex
      }));
      fetchCases(true);
    },
    [setTableState, fetchCases]
  );

  const handleChangeRowsPerPage = useCallback(
    event => {
      setTableState(state => ({
        ...state,
        pageIndex: 0,
        rowsPerPage: Number(event.target.value)
      }));
      fetchCases(true);
    },
    [setTableState, fetchCases]
  );

  const exportTableCases = useCallback(() => {
    exportCases(tableState.filters);
  }, [exportCases, tableState.filters]);

  const shareTableState = useCallback(() => {
    const searchParams = getUrlParams();

    const { origin, pathname } = window.location;
    navigator.clipboard
      .writeText(`${origin}${pathname}?${searchParams.toString()}`)
      .then(
        () => {
          addSnackbarToQueue({
            message: "URL copied to clipboard.",
            severity: "success"
          });
        },
        () => {
          addSnackbarToQueue({
            message: "URL failed to copy to clipboard.",
            severity: "error"
          });
        }
      );
  }, [addSnackbarToQueue, getUrlParams]);

  const handleHeaderCheckboxChange = useCallback(
    checked => {
      const newSelectedCaseIds = new Set(selectedCaseIds);

      // Only select cases visible on the table
      page.forEach(row => {
        const caseId = row.original.case_id;
        if (checked) {
          newSelectedCaseIds.delete(caseId);
        } else {
          newSelectedCaseIds.add(caseId);
        }
      });

      setSelectedCaseIds(newSelectedCaseIds);
    },
    [page, selectedCaseIds]
  );

  const handleHeaderClick = useCallback(
    (column, event) => {
      const actionElement = event.target.closest("[data-action]");
      if (actionElement) {
        const action = actionElement.dataset.action;
        if (action === "sort") {
          setSortBy(column, event);
        } else if (action === "group") {
          setGroupBy(column);
        }
      }
    },
    [setSortBy, setGroupBy]
  );

  const RenderHeaders = useCallback(
    headerGroups => {
      // TODO: Update how we handle selected rows to ensure we only select subrows when grouping.
      const selectedRowsCount = page.filter(row =>
        selectedCaseIds.has(row.original?.case_id)
      ).length;

      const checked = page.length > 0 && selectedRowsCount === page.length;
      const indeterminate =
        selectedRowsCount > 0 && selectedRowsCount < page.length;

      return headerGroups.map((headerGroup, index) => (
        <React.Fragment key={index}>
          <TableRow {...headerGroup.getHeaderGroupProps()}>
            {/* TODO: Need to update how to handle tablewide checkbox when grouping. Currently it is hidden when grouping applied. */}
            {tableState.groupBy.length === 0 && (
              <CheckboxHeaderCell>
                <Checkbox
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={() => handleHeaderCheckboxChange(checked)}
                />
              </CheckboxHeaderCell>
            )}
            {headerGroup.headers.map((column, i) => (
              <TableHeaderCell data-col={i + 1} {...column.getHeaderProps()}>
                <div>
                  <span onClick={event => handleHeaderClick(column, event)}>
                    {column.render("Header")}
                  </span>
                </div>
              </TableHeaderCell>
            ))}
          </TableRow>
          {casesLoading && (
            <TableRow>
              <TableLoadingCell colSpan={visibleColumns.length + 1}>
                <LinearProgress />
              </TableLoadingCell>
            </TableRow>
          )}
        </React.Fragment>
      ));
    },
    [
      casesLoading,
      visibleColumns.length,
      handleHeaderClick,
      page,
      handleHeaderCheckboxChange,
      selectedCaseIds,
      tableState.groupBy.length
    ]
  );

  const handleRowCheckboxChange = useCallback(
    (caseId, isSelected) => {
      const newSelectedCaseIds = new Set(selectedCaseIds);

      if (isSelected) {
        newSelectedCaseIds.add(caseId);
      } else {
        newSelectedCaseIds.delete(caseId);
      }

      setSelectedCaseIds(newSelectedCaseIds);
    },
    [selectedCaseIds]
  );

  const RenderRow = useCallback(
    (props, row) => {
      prepareRow(row);

      const isSelected = selectedCaseIds.has(row.original?.case_id);

      return (
        <TableBodyRow
          component={row.canExpand ? null : Link}
          to={`case-management/${row.original?.case_id}`}
          {...row.getRowProps(props)}
          selected={isSelected}
          onClick={() => {
            if (row.canExpand) toggleRowExpanded(row.id);
          }}
        >
          {!row.canExpand && (
            <TableBodyCell role="cell" style={{ width: "60px" }}>
              <Checkbox
                checked={isSelected}
                onClick={e => e.stopPropagation()}
                onChange={() =>
                  handleRowCheckboxChange(row.original.case_id, !isSelected)
                }
              />
            </TableBodyCell>
          )}
          {row.cells.map((cell, i) => (
            <TableBodyCell data-col={i + 1} {...cell.getCellProps()}>
              {/* If it's a grouped cell, add an expander and row count */}
              {cell.isGrouped && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span {...row.getToggleRowExpandedProps()}>
                    {row.isExpanded ? (
                      <ArrowDropDownIcon />
                    ) : (
                      <ArrowRightIcon />
                    )}
                  </span>
                  <span style={{ marginRight: "5px" }}>
                    {cell.render("Cell")}
                  </span>
                  <span>({row.subRows.length})</span>
                </div>
              )}
              {cell.isPlaceholder && null}
              {!cell.isGrouped && !cell.isPlaceholder && cell.render("Cell")}
            </TableBodyCell>
          ))}
        </TableBodyRow>
      );
    },
    [prepareRow, selectedCaseIds, handleRowCheckboxChange, toggleRowExpanded]
  );

  const LoadingCell = () => {
    return tableLoading ? (
      <TableRow>
        <TableLoadingCell colSpan={visibleColumns.length + 1}>
          <LinearProgress />
        </TableLoadingCell>
      </TableRow>
    ) : (
      <TableRow>
        <TableLoadingCell colSpan={visibleColumns.length + 1} />
      </TableRow>
    );
  };

  const CaseGroupingLoadingPlaceholder = () => (
    <TableHead>
      <LoadingCell />
      <TableRow>
        <TableBodyCell colSpan={headerGroups[0]?.headers?.length + 1 || 1}>
          <TablePlaceHolder>
            Grouping cases, this may take a moment…
          </TablePlaceHolder>
        </TableBodyCell>
      </TableRow>
    </TableHead>
  );

  return (
    <>
      <CaseTableFilters
        filters={tableState.filters}
        setFilters={setFilters}
        setFilterGroups={setFilterGroups}
        clearFilters={clearFilters}
      />
      <StyledPaper>
        <ProgressIndicatorOverlay loading={exportLoading} />
        <TableHeader>Results</TableHeader>
        <CaseTableSettings
          shareTableState={shareTableState}
          getUrlParams={getUrlParams}
          exportCases={exportTableCases}
          exportLoading={exportLoading}
          tableState={tableState}
          setTableState={setTableState}
          columns={allColumns}
          setColumns={setColumns}
          fetchCases={fetchCases}
          selectedCases={selectedCases}
        />
        <TableAndPaginationWrapper>
          <CaseTablePaper>
            <Table>
              {casesLoading && tableState.groupBy.length > 0 && (
                <CaseGroupingLoadingPlaceholder />
              )}
            </Table>

            {/* The TableVirtuoso component is used to create a virtualized table,
            which is a table that only renders the rows currently visible in the
            viewport. This is useful for performance reasons when dealing with
            large amounts of data. */}
            {/* The Case Table has been adapted using the following example: https://virtuoso.dev/tanstack-table-integration/ */}

            <TableVirtuoso
              totalCount={page.length}
              components={{
                // eslint-disable-next-line react/prop-types
                Table: ({ style, ...props }) => (
                  <CaseTable
                    data-qa="CM-Table-Cases"
                    ref={tableRef}
                    style={{ ...style }}
                    {...props}
                    {...getTableProps()}
                  />
                ),
                // eslint-disable-next-line react/prop-types, react/display-name
                TableBody: React.forwardRef(({ style, ...props }, ref) => {
                  if (!casesLoading && tableState.cases.length === 0) {
                    return (
                      <TableBody
                        data-qa_loading={tableLoading}
                        data-qa_load_count={tableLoadCount}
                        {...getTableBodyProps()}
                        {...props}
                        ref={ref}
                      >
                        <TableRow>
                          <TableBodyCell
                            colSpan={headerGroups[0]?.headers?.length + 1 || 1}
                          >
                            <TablePlaceHolder>No Data</TablePlaceHolder>
                          </TableBodyCell>
                        </TableRow>
                      </TableBody>
                    );
                  }

                  if (tableState.cases.length > 0) {
                    return (
                      <TableBody
                        data-qa_loading={tableLoading}
                        data-qa_load_count={tableLoadCount}
                        {...getTableBodyProps()}
                        {...props}
                        ref={ref}
                      />
                    );
                  }
                }),
                TableRow: props => {
                  // eslint-disable-next-line react/prop-types
                  const index = props["data-index"];
                  const row = page[index];

                  if (casesLoading && tableState.groupBy.length > 0) {
                    return;
                  }
                  return RenderRow(props, row);
                }
              }}
              fixedHeaderContent={() => {
                if (!(casesLoading && tableState.groupBy.length > 0)) {
                  return RenderHeaders(headerGroups);
                }
              }}
            />
          </CaseTablePaper>

          {/* If grouping is applied there is no need to paginate since all data available */}
          {tableState.groupBy.length <= 0 && (
            <Pagination
              rowCount={rows.length}
              casesCount={tableState.casesCount}
              pageSize={tableState.rowsPerPage}
              pageIndex={tableState.pageIndex}
              handlePageChange={handleChangePage}
              handleRowsPerPageChange={handleChangeRowsPerPage}
              TablePaginationActions={TablePaginationActions}
            />
          )}
        </TableAndPaginationWrapper>
      </StyledPaper>
    </>
  );
}

CaseManagementPage.propTypes = {};

const TableAndPaginationWrapper = styled("div")`
  border: 1px solid rgba(224, 224, 224, 1);
  border-radius: 4px;
`;

const CaseTablePaper = styled(Paper)`
  display: block;

  // Height change follow Filtering grid
  height: calc(100vh - 475px);
  min-height: 500px;
  @media (max-width: 1900px) {
    height: calc(100vh - 520px);
  }
  @media (max-width: 1200px) {
    height: calc(100vh - 690px);
  }
  @media (max-width: 960px) {
    height: calc(100vh - 520px);
  }
  @media (max-width: 860px) {
    height: calc(100vh - 690px);
  }
`;

const StyledPaper = styled(Paper)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
`;

const TableHeader = styled(Typography)`
  font-size: 18px;
  color: #555555;
`;

const CaseTable = styled(Table)`
  width: 100%;
  border-spacing: 0;
`;

const TableHeaderCell = styled(TableCell)`
  padding: 10px 0 10px 10px;
  font-weight: bold;
  text-align: left;
  vertical-align: top;
  background-color: #ffffff;
`;

const CheckboxHeaderCell = styled(TableHeaderCell)`
  padding: 0 0 10px 10px;
`;

const TableLoadingCell = styled(TableCell)`
  padding: 0;
  height: 5px;
  z-index: 1;
`;

const TableBodyRow = styled(TableRow)`
  height: 60px;

  &:hover {
    background-color: rgba(76, 175, 80, 0.5);
    cursor: pointer;
  }
`;

const TableBodyCell = styled(TableCell)`
  width: 1%;
  padding: 10px 0 10px 10px;
`;

const TablePlaceHolder = styled("div")`
  text-align: center;
`;

const CaseTableSettings = memo(function CaseTableSettings(props) {
  const {
    exportCases,
    getUrlParams,
    shareTableState,
    exportLoading,
    columns,
    setColumns,
    selectedCases,
    fetchCases
  } = props;

  const { hasFeature } = useContext(FeaturesContext);

  const selectedCaseIds = useMemo(() => {
    return selectedCases.map(selectedCase => selectedCase.case_id);
  }, [selectedCases]);

  const [showPrintCaseDialog, setShowPrintCaseDialog] = useState(false);

  const handlePrintCase = () => {
    setShowPrintCaseDialog(true);
  };

  const [
    showCreateMultipleCasesDialog,
    setShowCreateMultipleCasesDialog
  ] = useState(false);

  const handleCreateMultipleCasesDialogClose = () => {
    setShowCreateMultipleCasesDialog(false);
  };

  return (
    <>
      <SettingsContainer>
        <CaseTableOptionsContainer>
          <CaseTableOptions />
        </CaseTableOptionsContainer>
        <ButtonsContainer>
          <AddCaseButton
            data-qa="CM-Button-Add-Case"
            getUrlParams={getUrlParams}
          />

          <Button
            variant="contained"
            color="primary"
            data-qa="CM-Button-Case-Bulk-Create"
            onClick={() => setShowCreateMultipleCasesDialog(true)}
            startIcon={<LibraryAddIcon />}
          >
            Add Multiple Cases
          </Button>

          {// We only want to display the dropdown when the projects feature is enabled
          hasFeature("projects") && (
            <AddToProjectDropdown
              disabled={!selectedCases.length}
              data-qa="CM-Button-Add-To-Project"
              selectedCases={selectedCases.map(selectedCase => {
                const { case_id, ...rest } = selectedCase;
                // Replace `case_id` with `id` so that Case type works correctly in child component.
                return { ...rest, id: case_id };
              })}
              onProjectAdded={fetchCases}
            />
          )}
          <Tooltip title="Export all cases in csv format with current filters applied.">
            <Button
              data-qa="CM-Button-Case-Export"
              variant="outlined"
              color="primary"
              aria-label="Case Export Button"
              disabled={exportLoading}
              onClick={exportCases}
              startIcon={<FileDownloadIcon />}
            >
              Export
            </Button>
          </Tooltip>
          <Tooltip title="Download a printable report of selected cases, in the same order as the table.">
            <Button
              variant="outlined"
              data-qa="CM-Button-Case-Print"
              onClick={handlePrintCase}
              startIcon={<FileDownloadIcon />}
              disabled={!selectedCaseIds.length}
            >
              Report
            </Button>
          </Tooltip>
          <Tooltip title="Share your table via a link.">
            <Button
              data-qa="CM-Button-Share-Table"
              variant="outlined"
              color="primary"
              aria-label="Share Table Button"
              onClick={shareTableState}
              startIcon={<ShareIcon />}
            >
              Share
            </Button>
          </Tooltip>
          <CaseTableSettingsButton columns={columns} setColumns={setColumns} />
        </ButtonsContainer>
      </SettingsContainer>

      {showPrintCaseDialog && (
        <PrintCasesDialog
          caseIds={selectedCaseIds}
          isOpen={showPrintCaseDialog}
          onClose={() => setShowPrintCaseDialog(false)}
        />
      )}

      {showCreateMultipleCasesDialog && (
        <CreateMultipleCasesDialog
          isOpen={showCreateMultipleCasesDialog}
          onClose={handleCreateMultipleCasesDialogClose}
        />
      )}
    </>
  );
});

CaseTableSettings.propTypes = {
  exportCases: PropTypes.func.isRequired,
  exportLoading: PropTypes.bool.isRequired,
  getUrlParams: PropTypes.func.isRequired,
  shareTableState: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired,
  setColumns: PropTypes.func.isRequired,
  selectedCases: PropTypes.array.isRequired,
  fetchCases: PropTypes.func.isRequired
};

const SettingsContainer = styled("div")`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;

  // Fix the height of buttons and inputs so that they align and look correct
  .MuiButton-root,
  .MuiInputBase-root {
    height: 40px;
  }
`;

const CaseTableOptionsContainer = styled("div")`
  flex-grow: 1;
`;

const ButtonsContainer = styled("div")`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
`;

export default memo(CaseManagementPage);
